import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
	walletPaymentActions,
	walletPaymentSelectors,
	generalSelectors,
	walletPaymentTypes,
} from 'Ducks';
import { composeClassNames, convertRialToToman } from 'Helpers';
import Input from 'Components/Globals/Elements/Input/loadable';
import Icon from 'Components/Globals/Elements/Icon/loadable';
import Button from 'Components/Globals/Elements/Button';
import useServices from 'Hooks/useServices';
import GENERAL_REGEX from 'Helpers/regex';
import useContent from 'Hooks/useContent';

import styles from './styles.module.scss';
import strings from './strings';

function FactorAmount() {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { businessId, business } = useServices();

	const wallets = business?.wallets ?? [];
	const discountPercent =
		wallets.find((w) => w.is_active === true)?.discount_percent || 0;

	const defaultAmount = useSelector(walletPaymentSelectors.selectTotalAmount);
	const { formatMessage } = useContent();
	const [amount, setAmount] = useState(
		defaultAmount ? convertRialToToman(defaultAmount).replaceAll(',', '،') : ''
	);
	const [discountAmount, setDiscountAmount] = useState(
		defaultAmount ? (defaultAmount * discountPercent) / 100 : 0
	);
	const [balance, setBalance] = useState(
		defaultAmount ? defaultAmount - discountAmount : 0
	);
	const [totalAmount, setTotalAmount] = useState(
		defaultAmount ? defaultAmount : 0
	);
	const [keyboardOpen, setKeyboardOpen] = useState(false);
	const [keyboardBottomOffset, setKeyboardBottomOffset] = useState(0);
	const inputWrapperRef = useRef(null);
	const isLoading = useSelector((state) =>
		generalSelectors.selectApiLoadingStatus(
			state,
			walletPaymentTypes.NEW_TRANSACTION.REQUEST
		)
	);

	const onChangeInput = (val) => {
		let value = val.toString();
		value = value.replace(GENERAL_REGEX.removeNonDigit.pattern, '');
		value = value.replace(/^0+/, '');
		value = value.slice(0, 9);
		const discount = value ? Math.ceil((value * discountPercent) / 100) : 0;
		const nextBalance = (value - discount) * 10;
		if (nextBalance > 1000000000) {
			return;
		}
		setDiscountAmount(discount * 10);
		setTotalAmount(value * 10);
		setBalance(nextBalance);
		value = value.replace(GENERAL_REGEX.septateThreeCharacter.pattern, '،');
		setAmount(value);
	};
	const onSubmitAmount = () => {
		dispatch(
			walletPaymentActions.newTransactionRequest({
				amount: balance,
				totalAmount: totalAmount,
				business_id: businessId,
				business,
				navigate,
			})
		);
	};
	useEffect(() => {
		dispatch(walletPaymentActions.checkBarcodeClear());
	}, [dispatch]);

	useEffect(() => {
		let rafId;

		const tryFocus = () => {
			const wrapper = inputWrapperRef.current;
			if (!wrapper) {
				rafId = requestAnimationFrame(tryFocus);
				return;
			}
			const inputEl = wrapper.querySelector('input');
			if (inputEl && document.activeElement !== inputEl) {
				inputEl.focus();
			} else if (!inputEl) {
				rafId = requestAnimationFrame(tryFocus);
			}
		};

		rafId = requestAnimationFrame(tryFocus);
		return () => cancelAnimationFrame(rafId);
	}, []);

	useEffect(() => {
		if (!window.visualViewport) return;

		const updateKeyboardState = () => {
			const { height, offsetTop } = window.visualViewport;
			const keyboardVisible = height < window.innerHeight - 100;

			setKeyboardOpen(keyboardVisible);
			setKeyboardBottomOffset(
				keyboardVisible ? window.innerHeight - height - offsetTop : 0
			);
		};

		updateKeyboardState();
		window.visualViewport.addEventListener('resize', updateKeyboardState);
		window.visualViewport.addEventListener('scroll', updateKeyboardState);

		return () => {
			window.visualViewport.removeEventListener('resize', updateKeyboardState);
			window.visualViewport.removeEventListener('scroll', updateKeyboardState);
		};
	}, []);
	return (
		<div className={styles.wrapper}>
			<div className={styles.content}>
				<p className='txt-dark text-center m-16 mt-24'>
					{formatMessage(strings.amountDescription)}
				</p>

				<div ref={inputWrapperRef}>
					<Input
						name='amount'
						className={composeClassNames(['mv-8 mh-16', styles.input])}
						value={amount}
						label={formatMessage(strings.inputLabel)}
						onChange={onChangeInput}
						endIcon='toman'
						textCenter
						type='tel'
						inputMode='numeric'
						autoFocus
					/>
				</div>

				{Boolean(discountPercent) && balance > 0 && (
					<>
						<div className={`mt-16 ${styles.discount_wrapper}`}>
							<p className='sm txt-primary line-height-24 bold'>
								{formatMessage(strings.discountAmountTitle, {
									discountPercent,
								})}
							</p>

							<div className={styles.discount_amount_wrapper}>
								<p className='txt-primary md line-height-24 bold'>
									{convertRialToToman(discountAmount).replaceAll(',', '،')}
								</p>

								<Icon
									name='toman'
									size='xs'
									className='txt-primary'
								/>
							</div>
						</div>

						<div className={`mt-6 ${styles.discount_wrapper}`}>
							<p className='sm txt-dark line-height-24 bold'>
								{formatMessage(strings.finalAmountTitle)}
							</p>

							<div className={styles.discount_amount_wrapper}>
								<p className='txt-dark md line-height-24 bold'>
									{convertRialToToman(balance).replaceAll(',', '،')}
								</p>

								<Icon
									name='toman'
									size='xs'
									className='txt-dark'
								/>
							</div>
						</div>
					</>
				)}
			</div>

			<Button
				label={formatMessage(strings.buttonLabel)}
				onClick={onSubmitAmount}
				className={composeClassNames([
					'm-16',
					styles.button,
					keyboardOpen && styles.buttonKeyboard,
				])}
				style={
					keyboardOpen ? { bottom: `${keyboardBottomOffset}px` } : undefined
				}
				disabled={!amount || balance < 100000}
				isLoading={isLoading}
			/>
		</div>
	);
}

export default FactorAmount;
